import { Flex } from "@/atoms"

import {
  IntroducedThroughCard,
  IntroducedThroughCollapse
} from "../IntroducedThroughCard"
import { HistoryCommandsList } from "./HistoryCommandsList/HistoryCommandsList"

import styles from "./ImageIntroduceThrough.module.scss"

import type { ImageIntroducedThrough } from "@/domain/issue"
import type {
  getImportTree,
  HistoryImagePackageData,
  ImportItem
} from "@/domain/package"
import type { TFunction } from "@/hooks/useTranslation"
export type ImageIntroduceThroughProps = {
  introducedThrough: ImageIntroducedThrough[]
  importTree?: ReturnType<typeof getImportTree<ImportItem>>
}

// Every entry is a real introduction path, so every entry contributes its locations.
function getImagePackageLocations(images: ImageIntroducedThrough[]): string[] {
  const uniqueLocations = new Set<string>()

  images.forEach((image) => {
    image.packageLocations?.forEach((location) => uniqueLocations.add(location))
  })

  return Array.from(uniqueLocations)
}

type HistoryGroup = {
  baseImageName?: string
  historyEntries: HistoryImagePackageData[]
}

// One history section per base image: copies from different layers would otherwise each get
// their own section.
function groupHistoryByBaseImage(
  images: ImageIntroducedThrough[]
): HistoryGroup[] {
  const groups = new Map<
    string,
    HistoryGroup & { seenEntryKeys: Set<string> }
  >()

  images.forEach((image) => {
    if (!image.historyEntries?.length) return

    const baseImageName = image.baseImageDetails?.baseImageNames?.[0]
    const key = baseImageName ?? ""
    const group = groups.get(key) ?? {
      baseImageName,
      historyEntries: [],
      seenEntryKeys: new Set<string>()
    }
    if (!groups.has(key)) groups.set(key, group)

    image.historyEntries.forEach((entry) => {
      const entryKey = `${entry.layerIndex}\u0000${entry.createdBy}`
      if (group.seenEntryKeys.has(entryKey)) return
      group.seenEntryKeys.add(entryKey)
      group.historyEntries.push(entry)
    })
  })

  return Array.from(groups.values(), ({ baseImageName, historyEntries }) => ({
    baseImageName,
    historyEntries: historyEntries.sort((a, b) => a.layerIndex - b.layerIndex)
  }))
}

export const ImageIntroduceThrough: React.FC<
  ImageIntroduceThroughProps & { t: TFunction; tKey: string }
> = ({ introducedThrough, importTree, t, tKey }) => {
  // Copy first: sorting the prop in place mutates it for the finding's other consumers.
  const sortedIntroducedThrough = [...introducedThrough].sort(
    (a, b) =>
      (b.baseImageDetails?.baseImageLayerIndex ?? 0) -
      (a.baseImageDetails?.baseImageLayerIndex ?? 0)
  )
  const introducedThroughWithoutEmptyBaseImage = sortedIntroducedThrough.filter(
    (image) => image.baseImageDetails?.baseImageDigest
  )
  const packageLocations = getImagePackageLocations(sortedIntroducedThrough)
  const historyGroups = groupHistoryByBaseImage(sortedIntroducedThrough)

  return (
    <Flex vertical className={styles.wrapper} gap={16}>
      <IntroducedThroughCard
        images={introducedThroughWithoutEmptyBaseImage}
        importTree={importTree}
        locations={packageLocations}
        isOnPrem={false}
      />
      {historyGroups.map(({ baseImageName, historyEntries }) => {
        const displayName = baseImageName ? `(${baseImageName})` : ""
        const prefix = `inventory.images.drawer.issue`

        return (
          <IntroducedThroughCollapse
            key={baseImageName ?? ""}
            variant="flush"
            openLabel={`${t(`${prefix}.showDockerCommandHistory`)} ${displayName}`}
            closeLabel={`${t(`${prefix}.hideDockerCommandHistory`)} ${displayName}`}
          >
            <HistoryCommandsList
              historyEntries={historyEntries}
              tKey={tKey}
              t={t}
            />
          </IntroducedThroughCollapse>
        )
      })}
    </Flex>
  )
}
